import { AxiosProgressEvent, AxiosRequestConfig } from 'axios'

import BaseApi from '@/api/Base/BaseApi'
import {
  CommonListPayload,
  CreateDatasetNodePayload,
  DatasetNodeInfo,
  DatasetNodeItem,
  DatasetNodeItemListResponse,
  DeleteDatasetNodeItemPayload,
  DeleteItemsPayload,
  EditDatasetNodePayload,
  ImportCvatPayload,
  ImportCvatResponse,
  ImportStorageItemsToDatasetPayload,
  UploadFilesToDatasetPayload,
  UploadFilesToDatasetResponse,
  UploadRawFilePayload,
  UploadRawFileResponse
} from '@/api/DatasetNode/types'

const ROOT = '/project'
const getPrefix = (projectId: string, datasetNodeId?: string) => {
  if (datasetNodeId) {
    return `${ROOT}/${projectId}/dataset/${datasetNodeId}`
  }
  return `${ROOT}/${projectId}/dataset`
}
const defaultPaginationOptions = {
  itemsPerPage: 40,
  page: 0,
  sortDir: 'desc',
  sortKey: 'createdAt',
}

export default class DatasetNodeApi extends BaseApi {
  // create dataset node
  createDatasetNode(payload: CreateDatasetNodePayload) {
    const { projectId, ...rest } = payload
    return this.agent.post<DatasetNodeInfo>(`${getPrefix(projectId)}/create`, rest)
  }

  uploadFilesToDataset(payload: UploadFilesToDatasetPayload, onUploadProgress: (progressEvent: AxiosProgressEvent) => void) {
    const { datasetNodeId, files } = payload

    const formData = new FormData()
    files.forEach((file) => {
      formData.append('file', file)
    })

    const config: AxiosRequestConfig = {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    }

    return this.agent.post<UploadFilesToDatasetResponse>(`/dataset/${datasetNodeId}/upload-items`, formData, config)
  }

  importStorageItemsToDataset(payload: ImportStorageItemsToDatasetPayload) {
    const { datasetNodeId, ...rest } = payload
    return this.agent.post<DatasetNodeInfo>(`/dataset/${datasetNodeId}/import-storage-items`, rest)
  }

  importCvat(payload: ImportCvatPayload) {
    const {
      projectId,
      file,
      modelType,
      datasetNodeName = '',
      labelingNodeName = '',
      datasetNodeMetadata,
      datasetNodePosition,
      labelingNodeMetadata,
      labelingNodePosition,
    } = payload
    const formData = new FormData()
    formData.append('file', file)
    formData.append('modelType', modelType)
    formData.append('datasetNodeName', datasetNodeName)
    formData.append('labelingNodeName', labelingNodeName)
    formData.append('datasetNodeMetadata', JSON.stringify(datasetNodeMetadata))
    formData.append('datasetNodePosition', JSON.stringify(datasetNodePosition))
    formData.append('labelingNodeMetadata', JSON.stringify(labelingNodeMetadata))
    formData.append('labelingNodePosition', JSON.stringify(labelingNodePosition))
    return this.agent.post<ImportCvatResponse>(`/project/${projectId}/import-cvat`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  }

  // dataset node

  getDatasetNodeDetail(datasetNodeId: string) {
    return this.agent.get<DatasetNodeInfo>(`/dataset/${datasetNodeId}`)
  }

  getDatasetNodeData(datasetNodeId: string, options?: CommonListPayload) {
    const { itemsPerPage = 40, page = 0, sortDir = 'asc', sortKey = 'createdAt' } = options ?? {}
    return this.agent.get<DatasetNodeItemListResponse>(`/dataset/${datasetNodeId}/items`, {
      params: {
        page,
        num_per_page: itemsPerPage,
        sort_key: sortKey,
        sort_dir: sortDir,
      },
    })
  }

  editDatasetNode(payload: EditDatasetNodePayload) {
    const { id, name, ...rest } = payload

    return this.agent.put<DatasetNodeInfo>(`/dataset/${id}`, { ...rest, name: name?.trim() })
  }

  deleteDatasetNode(datasetNodeId: string) {
    return this.agent.delete<DatasetNodeInfo>(`/dataset/${datasetNodeId}`)
  }

  // dataset item
  addItemsByUpload(payload: UploadFilesToDatasetPayload, onUploadProgress: (progressEvent: AxiosProgressEvent) => void) {
    const { datasetNodeId, files } = payload

    const formData = new FormData()
    files.forEach((f) => {
      formData.append('file', f)
    })

    const config: AxiosRequestConfig = {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    }

    return this.agent.post<UploadFilesToDatasetResponse>(`/dataset/${datasetNodeId}/add-items`, formData, config)
  }

  addItemsFromStorage(payload: ImportStorageItemsToDatasetPayload) {
    const { datasetNodeId, ...rest } = payload
    return this.agent.post<DatasetNodeInfo>(`/dataset/${datasetNodeId}/add-storage-items`, rest)
  }

  deleteItems(payload: DeleteItemsPayload) {
    const { datasetNodeId, ...rest } = payload
    return this.agent.delete<DatasetNodeInfo>(`/dataset/${datasetNodeId}/delete-items`, { data: rest })
  }

  deleteDatasetNodeData(payload: DeleteDatasetNodeItemPayload) {
    const { datasetItemId } = payload
    return this.agent.delete(`/dataset-item/${datasetItemId}`)
  }

  hasConnectedNodes(datasetNodeId: string) {
    return this.agent.get<boolean>(`/dataset/${datasetNodeId}/has-connected-node`)
  }

  getDatasetItem(datasetItemId: string) {
    return this.agent.get<DatasetNodeItem>(`/dataset-item/${datasetItemId}`)
  }

  uploadRawFile(payload: UploadRawFilePayload, onUploadProgress: (progressEvent: AxiosProgressEvent) => void) {
    const { datasetNodeId, file, width, height, extension, pixelType } = payload

    const formData = new FormData()
    formData.append('file', file)
    formData.append('width', width)
    formData.append('height', height)
    formData.append('extension', extension)
    formData.append('pixel_type', pixelType)

    const config: AxiosRequestConfig = {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    }

    return this.agent.post<UploadRawFileResponse>(`/dataset/${datasetNodeId}/raw-upload`, formData, config)
  }
}
